import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { HERO_HEADLINES, HERO_DESCRIPTIONS } from '@/lib/constant';
import type { HeroSectionProps, VectorDBVisualizationProps } from "@/types/components";
import { DEFAULT_HERO_ANIMATION } from "@/types/components";

const VectorDBVisualization = lazy(() => 
  import("../loading/VectorDBVisualization")
    .then((module): { default: React.ComponentType<VectorDBVisualizationProps> } => ({
      default: module.default
    }))
);

const CONTENT = {
  VIDEO_URL: "https://res.cloudinary.com/dqql2wlbt/video/upload/v1752941137/pulsegene-new_hywgsv.mp4?q_auto"
} as const;

const HeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [effectType, setEffectType] = useState<'typewriter' | 'glitch' | 'morph' | 'fade'>('typewriter');
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const rotateHeadline = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      const nextIndex = (currentIndex + 1) % HERO_HEADLINES.length;
      const effects: Array<'typewriter' | 'glitch' | 'morph' | 'fade'> = ['typewriter', 'glitch', 'morph', 'fade'];
      const nextEffect = effects[nextIndex % effects.length];
      
      setEffectType(nextEffect);
      setCurrentIndex(nextIndex);
      
      if (nextEffect === 'typewriter') {
        typewriterEffect(HERO_HEADLINES[nextIndex]);
      } else {
        setTimeout(() => setIsAnimating(false), DEFAULT_HERO_ANIMATION.duration * 1000);
      }
    }
  }, [isAnimating, currentIndex]);

  const typewriterEffect = useCallback((text: string) => {
    setIsTyping(true);
    setDisplayText('');
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayText(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        setIsTyping(false);
        setIsAnimating(false);
      }
    }, 50);
  }, []);

  useEffect(() => {
    const timer = setInterval(rotateHeadline, DEFAULT_HERO_ANIMATION.interval);
    return () => clearInterval(timer);
  }, [rotateHeadline]);

  return (
    <section className={`relative min-h-screen overflow-hidden ${className}`}>
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute inset-0 bg-black/60 z-[1]" />
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          preload="auto"
          onLoadedData={() => setIsVideoLoaded(true)}
        >
          <source src={CONTENT.VIDEO_URL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Main Content */}
      <div className="relative z-[3] container mx-auto px-4 h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full">
          {/* Left Content */}
          <div className="flex flex-col justify-center space-y-8">
            <div className="relative mb-8 min-h-[200px] md:min-h-[240px] lg:min-h-[280px] flex items-center justify-center">
              {effectType === 'typewriter' && (
                <div className="text-center">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold overflow-hidden">
                    <span className="inline-block">
                      {displayText}
                    </span>
                    <span className="inline-block w-0.5 h-10 bg-blue-400 ml-1 animate-pulse"></span>
                  </h1>
                </div>
              )}
              
              {effectType === 'glitch' && (
                <motion.div
                  key={`glitch-${currentIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center relative"
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold relative">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-yellow-400 to-orange-400">
                      {HERO_HEADLINES[currentIndex]}
                    </span>
                    <span className="absolute inset-0 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400 animate-pulse opacity-30 transform -translate-x-1">
                      {HERO_HEADLINES[currentIndex]}
                    </span>
                  </h1>
                </motion.div>
              )}
              
              {effectType === 'morph' && (
                <motion.div
                  key={`morph-${currentIndex}`}
                  initial={{ scale: 0.8, rotateX: 90, opacity: 0 }}
                  animate={{ scale: 1, rotateX: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "backOut" }}
                  className="text-center"
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-red-400">
                      {HERO_HEADLINES[currentIndex]}
                    </span>
                  </h1>
                </motion.div>
              )}
              
              {effectType === 'fade' && (
                <motion.div
                  key={`fade-${currentIndex}`}
                  initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="text-center"
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400">
                      {HERO_HEADLINES[currentIndex]}
                    </span>
                  </h1>
                </motion.div>
              )}
            </div>

            {/* Description */}
            <div className="relative min-h-24 md:min-h-20 overflow-hidden">
              <motion.p
                key={currentIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="text-base md:text-lg text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: HERO_DESCRIPTIONS[currentIndex] }}
              />
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <motion.a
                href="#contact"
                className="
                  inline-flex items-center gap-2
                  px-6 py-3 rounded-full text-lg font-medium
                  bg-gradient-to-r from-blue-500 to-emerald-500
                  hover:from-blue-600 hover:to-emerald-600
                  text-white
                  transition-all duration-300
                  shadow-[0_0_20px_rgba(59,130,246,0.3)]
                  hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]
                "
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.a>
            </motion.div>
          </div>

          {/* Right Content - 3D Visualization */}
          <div className="hidden lg:block relative h-[600px] w-full">
            <div className="absolute inset-0 flex items-center justify-center scale-75 translate-x-12">
              <Suspense fallback={<div className="w-full h-full bg-gray-800/20 rounded-2xl animate-pulse" />}>
                <VectorDBVisualization 
                  className="w-full h-full"
                  canvaStyles={{ 
                    height: "100%", 
                    width: "100%",
                    transform: "scale(1.4) translateY(-10px)"
                  }} 
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        <motion.div 
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,255,0.1),transparent_70%)]"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear"
          }}
        />
        <div className="absolute inset-0 w-full h-full bg-grid opacity-10" />
      </div>
    </section>
  );
};

export default HeroSection;
